import React from 'react';

import ClaudeIcon from 'assets/images/central_ia/claude.svg';
import DeepseekIcon from 'assets/images/central_ia/deepseek.svg';
import GeminiIcon from 'assets/images/central_ia/gemini.svg';
import ChatGptIcon from 'assets/images/central_ia/chatgpt.svg';
import PerplexityIcon from 'assets/images/central_ia/perplexity.svg';
import MetaIcon from 'assets/images/central_ia/meta.svg';
import QwenIcon from 'assets/images/central_ia/qwen.svg';
import MaritacaIcon from 'assets/images/central_ia/maritaca.png';
import MistralIcon from 'assets/images/central_ia/mistral.svg';
import GrokIcon from 'assets/images/central_ia/grok.svg';
import CohereIcon from 'assets/images/central_ia/cohere.svg';
import KimiIcon from 'assets/images/central_ia/kimi.svg';

import { Container, ProviderCard } from './styles';

export interface ProviderVersion {
  id: string;
  label: string;
}

export interface IAProvider {
  id: string;
  name: string;
  icon: string;
  model: string;
  versions: ProviderVersion[];
}

export const providers: IAProvider[] = [
  {
    id: 'openai',
    name: 'Chat GPT',
    icon: ChatGptIcon,
    model: 'openai',
    versions: [
      { id: 'gpt-5.6-terra', label: 'Chat GPT 5.6' },
      { id: 'gpt-5.6-luna', label: 'Chat GPT 5.6 Luna' },
    ],
  },
  {
    id: 'claude',
    name: 'Claude',
    icon: ClaudeIcon,
    model: 'claude',
    versions: [
      { id: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
      { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
    ],
  },
  {
    id: 'gemini',
    name: 'Gemini',
    icon: GeminiIcon,
    model: 'gemini',
    versions: [
      { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
      { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
    ],
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    icon: PerplexityIcon,
    model: 'perplexity',
    versions: [
      { id: 'sonar', label: 'Perplexity Sonar' },
      { id: 'sonar-pro', label: 'Perplexity Sonar Pro' },
    ],
  },
  {
    id: 'deepseek',
    name: 'Deepseek',
    icon: DeepseekIcon,
    model: 'deepseek',
    versions: [
      { id: 'deepseek-v4-flash', label: 'Deepseek V4 Flash' },
      { id: 'deepseek-v4-pro', label: 'Deepseek V4 Pro' },
    ],
  },
  {
    id: 'grok',
    name: 'Grok',
    icon: GrokIcon,
    model: 'grok',
    versions: [
      { id: 'grok-4.5', label: 'Grok 4.5' },
      { id: 'grok-4.3', label: 'Grok 4.3' },
    ],
  },
  {
    id: 'llama',
    name: 'Meta Llama',
    icon: MetaIcon,
    model: 'llama',
    versions: [
      { id: 'us.meta.llama4-maverick-17b-instruct-v1:0', label: 'Llama 4 Maverick' },
      { id: 'us.meta.llama4-scout-17b-instruct-v1:0', label: 'Llama 4 Scout' },
    ],
  },
  {
    id: 'qwen3',
    name: 'Qwen3',
    icon: QwenIcon,
    model: 'qwen3',
    versions: [
      { id: 'qwen3.7-plus', label: 'Qwen 3.7 Plus' },
      { id: 'qwen3.7-max', label: 'Qwen 3.7 Max' },
      { id: 'qwen3.7-flash', label: 'Qwen 3.7 Flash' },
    ],
  },
  {
    id: 'maritaca',
    name: 'Maritaca',
    icon: MaritacaIcon,
    model: 'maritaca',
    versions: [
      { id: 'sabia-4', label: 'Sabiá 4' },
      { id: 'sabiazinho-4', label: 'Sabiazinho 4' },
    ],
  },
  {
    id: 'cohere',
    name: 'Cohere',
    icon: CohereIcon,
    model: 'cohere',
    versions: [
      { id: 'command-a-03-2025', label: 'Command A' },
      { id: 'command-a-vision-07-2025', label: 'Command A Vision' },
    ],
  },
  {
    id: 'mistral',
    name: 'Mistral',
    icon: MistralIcon,
    model: 'mistral',
    versions: [
      { id: 'mistral-large-latest', label: 'Mistral Large' },
      { id: 'mistral-medium-latest', label: 'Mistral Medium' },
    ],
  },
  {
    id: 'kimi',
    name: 'Kimi',
    icon: KimiIcon,
    model: 'kimi',
    versions: [
      { id: 'kimi-k2.6', label: 'Kimi K2.6' },
      { id: 'kimi-k3', label: 'Kimi K3' },
    ],
  },
];

export const getDefaultVersion = (providerId: string): string =>
  providers.find((p) => p.id === providerId)?.versions[0]?.id || '';

export const getVersionLabel = (modelId: string): string => {
  let label = '';
  providers.forEach((provider) => {
    const version = provider.versions.find((v) => v.id === modelId);
    if (version) label = version.label;
  });
  return label;
};

interface CentralIaProviderSelectorProps {
  selectedProvider: string;
  onSelectProvider: (providerId: string) => void;
}

const CentralIaProviderSelector: React.FC<CentralIaProviderSelectorProps> = ({
  selectedProvider,
  onSelectProvider,
}) => {
  return (
    <Container>
      <div className="providers-grid">
        {providers.map((provider) => (
          <ProviderCard
            key={provider.id}
            type="button"
            selected={selectedProvider === provider.id}
            onClick={() => onSelectProvider(provider.id)}
          >
            <img className="provider-icon" src={provider.icon} alt={provider.name} />
            <span className="provider-name font-weight-light">{provider.name}</span>
          </ProviderCard>
        ))}
      </div>
    </Container>
  );
};

export default CentralIaProviderSelector;
